import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Pencil, X, Camera } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfileSelect() {
  const { user, setActiveProfile } = useAuth();
  const profiles = user?.profiles || [];
  const [managing, setManaging] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProfileName, setNewProfileName] = useState('');
  const [newProfileKind, setNewProfileKind] = useState('adult');
  const [addError, setAddError] = useState('');
  const [gallery, setGallery] = useState([]);
  const [selectedAvatar, setSelectedAvatar] = useState('');
  const navigate = useNavigate();

  const openAddForm = () => {
    setAddError('');
    setNewProfileName('');
    setSelectedAvatar('');
    setShowAddForm(true);
    if (!gallery.length) {
      axios.get(`${API}/avatar-gallery`, { withCredentials: true }).then(r => setGallery(r.data || [])).catch(() => {});
    }
  };

  const handleSelectProfile = async (profile) => {
    if (managing) {
      navigate(`/account/settings?tab=profiles&profile=${profile.id}`);
    } else {
      await setActiveProfile(profile);
    }
  };

  const handleAddProfile = async () => {
    if (!newProfileName.trim()) return;
    try {
      await axios.post(`${API}/profiles`, { name: newProfileName.trim(), avatar_url: selectedAvatar, kind: newProfileKind }, { withCredentials: true });
      setNewProfileName('');
      setShowAddForm(false);
      window.location.reload();
    } catch (err) {
      setAddError(err.response?.data?.detail || "Impossible de créer le profil");
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ background: 'linear-gradient(180deg, #11041F 0%, #382C4E 100%)' }}>
      <h1 className="text-[52px] sm:text-[69px] font-bold text-white mb-14 animate-fadeIn leading-tight" data-testid="profile-select-title">
        {managing ? 'Gestion des profils :' : 'Qui est-ce ?'}
      </h1>

      <div className="flex flex-wrap justify-center gap-7 sm:gap-10 stagger-children">
        {profiles.map((profile, idx) => (
          <button
            key={profile.id}
            data-testid={`profile-${idx}`}
            onClick={() => handleSelectProfile(profile)}
            className="group flex flex-col items-center gap-5"
          >
            <div className="relative w-[173px] h-[173px] sm:w-[202px] sm:h-[202px] rounded-lg overflow-hidden border-2 border-transparent group-hover:border-[#E9641A] transition-all duration-300">
              <img
                src={profile.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(profile.name)}&background=1a0e2e&color=E9641A&size=280`}
                alt={profile.name}
                className={`w-full h-full object-cover transition-all duration-300 ${managing ? 'brightness-50' : 'group-hover:scale-105'}`}
              />
              {managing && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <Pencil size={46} className="text-white drop-shadow-lg" />
                </div>
              )}
            </div>
            <span className="text-[20px] text-[#A1A1AA] group-hover:text-white transition-colors font-medium">
              {profile.name}
            </span>
          </button>
        ))}

        <button data-testid="add-profile-btn" onClick={openAddForm}
            className="group flex flex-col items-center gap-5">
            <div className="w-[173px] h-[173px] sm:w-[202px] sm:h-[202px] rounded-lg border-2 border-white/20 group-hover:border-[#E9641A] flex items-center justify-center transition-all duration-300 bg-white/5">
              <Plus size={63} className="text-white/40 group-hover:text-[#E9641A] transition-colors" />
            </div>
            <span className="text-[20px] text-[#A1A1AA] group-hover:text-white transition-colors font-medium">Ajouter</span>
          </button>
      </div>

      <button
        data-testid="manage-profiles-btn"
        onClick={() => setManaging(!managing)}
        className={`mt-14 px-[58px] py-[17px] border text-[23px] font-medium transition-all ${
          managing
            ? 'border-white bg-white text-[#11041F] hover:bg-white/90'
            : 'border-white/30 text-white/70 hover:text-white hover:border-white/60'
        }`}
      >
        {managing ? 'Terminé' : 'Gérer les profils'}
      </button>

      {/* Add Profile Modal */}
      {showAddForm && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center animate-fadeIn" onClick={() => setShowAddForm(false)}>
          <div className="bg-[#1a0e2e] border border-white/10 rounded-xl p-6 w-[540px] max-w-[92vw] animate-popIn" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-white mb-4">Nouveau profil</h3>
            <p className="text-sm text-white/60 mb-2">Type de profil</p>
            <div className="grid grid-cols-2 gap-3 mb-5">
              <button type="button" data-testid="profile-kind-adult" onClick={() => setNewProfileKind('adult')}
                className={`rounded-lg border-2 p-3 text-left transition-all ${newProfileKind === 'adult' ? 'border-[#E9641A] bg-[#E9641A]/10' : 'border-white/10 bg-white/5 hover:border-white/30'}`}>
                <p className="text-sm font-bold text-white">Adulte</p>
                <p className="text-[11px] text-white/50 mt-0.5">Accès à tout le catalogue</p>
              </button>
              <button type="button" data-testid="profile-kind-kids" onClick={() => setNewProfileKind('kids')}
                className={`rounded-lg border-2 p-3 text-left transition-all ${newProfileKind === 'kids' ? 'border-[#75D4D5] bg-[#75D4D5]/10' : 'border-white/10 bg-white/5 hover:border-white/30'}`}>
                <p className="text-sm font-bold text-white">Enfant</p>
                <p className="text-[11px] text-white/50 mt-0.5">Uniquement les films et séries jeunesse</p>
              </button>
            </div>
            <div className="flex items-center gap-4 mb-4">
              <div className="w-16 h-16 rounded-lg overflow-hidden bg-white/5 border border-white/10 flex-shrink-0" data-testid="new-profile-avatar-preview">
                {selectedAvatar ? (
                  <img src={selectedAvatar} alt="Avatar choisi" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/20"><Camera size={24} /></div>
                )}
              </div>
              <input
                data-testid="new-profile-name"
                type="text" value={newProfileName} onChange={e => setNewProfileName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleAddProfile(); }}
                placeholder="Nom du profil"
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#E9641A]"
                autoFocus
              />
            </div>
            {gallery.length > 0 && (
              <div className="mb-4">
                <p className="text-sm text-white/60 mb-2">Choisissez un avatar <span className="text-white/30">(facultatif — sinon un avatar sera attribué automatiquement)</span></p>
                <div className="max-h-56 overflow-y-auto pr-1 space-y-3" data-testid="avatar-gallery-grid">
                  {gallery.map(cat => (
                    <div key={cat.id}>
                      {cat.name && <p className="text-xs uppercase tracking-wide text-white/30 mb-1.5">{cat.name}</p>}
                      <div className="grid grid-cols-6 gap-2">
                        {(cat.avatars || []).map((url, i) => (
                          <button
                            key={url}
                            type="button"
                            data-testid={`avatar-option-${cat.id}-${i}`}
                            onClick={() => setSelectedAvatar(url === selectedAvatar ? '' : url)}
                            className={`relative aspect-square rounded-lg overflow-hidden border-2 transition-all duration-150 ${
                              selectedAvatar === url ? 'border-[#E9641A] scale-105 shadow-lg shadow-[#E9641A]/30' : 'border-transparent hover:border-white/40'
                            }`}
                          >
                            <img src={url} alt="" className="w-full h-full object-cover" loading="lazy" />
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {addError && (
              <div className="flex items-start gap-2 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2.5 mb-4" data-testid="add-profile-error">
                <span className="text-red-400 text-xs leading-relaxed">{addError}</span>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button onClick={() => setShowAddForm(false)} className="px-4 py-2 text-sm text-white/50 hover:text-white">Annuler</button>
              <button data-testid="confirm-add-profile" onClick={handleAddProfile}
                className="px-4 py-2 text-sm bg-[#E9641A] hover:bg-[#FE975E] text-white rounded-lg font-semibold transition-colors">Ajouter</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
